/**
 * Правила начисления выполнения компании (ПЛ-36): шаги с весами по виду работ. Видят все, кто видит график;
 * правит администратор справочников (право «Вести юрлица группы и справочники»).
 */
import { type ProgressRuleRow, STEP_CLOSERS, type StepCloser } from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Trash2 } from 'lucide-react';
import { useState } from 'react';
import { api } from '../../lib/api';
import { useCan } from '../../lib/session';
import { Button, IconButton } from '../../ui/button';
import { Dialog } from '../../ui/dialog';
import { ErrorBox, Spinner, toast } from '../../ui/feedback';
import { Input, Select } from '../../ui/form';
import { Panel } from '../../ui/layout';
import { LField } from './LField';

type Step = { name: string; weight: string; closeBy: StepCloser };
const EXAMPLE: Step[] = [
  { name: 'Доставка', weight: '10', closeBy: 'manual' },
  { name: 'Монтаж', weight: '60', closeBy: 'report' },
  { name: 'Сварка и контроль', weight: '20', closeBy: 'control' },
  { name: 'Испытание', weight: '10', closeBy: 'act' },
];

export function ProgressRules() {
  const edit = useCan('admin.org');
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['progress-rules'],
    queryFn: () => api<ProgressRuleRow[]>('/progress-rules'),
  });
  const [open, setOpen] = useState<ProgressRuleRow | 'new' | null>(null);
  const del = useMutation({
    mutationFn: (id: string) => api(`/progress-rules/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['progress-rules'] }),
    onError: (e) => toast(e.message, 'danger'),
  });
  return (
    <Panel
      title="Правила начисления выполнения"
      meta="процент работы — по закрытым шагам"
      actions={
        edit ? (
          <Button onClick={() => setOpen('new')} className="max-md:h-11">
            + Новое правило
          </Button>
        ) : null
      }
    >
      {q.isPending ? <Spinner /> : null}
      {q.isError ? <ErrorBox error={q.error} /> : null}
      {q.data && !q.data.length ? (
        <p className="text-[14px] text-ink-2">
          Правил пока нет.{' '}
          {edit
            ? 'Заведите правило по виду работ: например, трубопровод — доставка 10 %, монтаж 60 %, сварка и контроль 20 %, испытание 10 %.'
            : 'Их заводит администратор справочников.'}
        </p>
      ) : null}
      <ul className="divide-y divide-line text-[14px]">
        {(q.data ?? []).map((r) => (
          <li key={r.id} className="flex flex-wrap items-center justify-between gap-2 py-1.5">
            <button
              type="button"
              disabled={!edit}
              onClick={() => setOpen(r)}
              className="min-h-11 min-w-0 text-left enabled:hover:underline md:min-h-0"
            >
              <span className="font-medium">{r.name}</span>
              {r.workType ? <span className="text-ink-2"> · {r.workType}</span> : null}
              <span className="block text-[12px] text-ink-2">
                {r.steps.map((s) => `${s.name} ${String(s.weight).replace('.', ',')} %`).join(' · ')}
              </span>
            </button>
            {edit ? (
              <IconButton
                label="Удалить правило"
                onClick={() =>
                  window.confirm(`Удалить правило «${r.name}»? Шаги работ останутся.`) && del.mutate(r.id)
                }
                className="max-md:size-11"
              >
                <Trash2 size={16} />
              </IconButton>
            ) : null}
          </li>
        ))}
      </ul>
      {open ? <RuleDialog rule={open === 'new' ? null : open} onClose={() => setOpen(null)} /> : null}
    </Panel>
  );
}

function RuleDialog({ rule, onClose }: { rule: ProgressRuleRow | null; onClose: () => void }) {
  const qc = useQueryClient();
  const [name, setName] = useState(rule?.name ?? '');
  const [workType, setWorkType] = useState(rule?.workType ?? '');
  const [steps, setSteps] = useState<Step[]>(
    rule ? rule.steps.map((s) => ({ ...s, weight: String(s.weight).replace('.', ',') })) : EXAMPLE,
  );
  const w = (s: string) => Number(s.replace(',', '.'));
  const total =
    Math.round(steps.reduce((n, s) => n + (Number.isFinite(w(s.weight)) ? w(s.weight) : 0), 0) * 100) / 100;
  const save = useMutation({
    mutationFn: () =>
      api<ProgressRuleRow>(rule ? `/progress-rules/${rule.id}` : '/progress-rules', {
        method: rule ? 'PATCH' : 'POST',
        body: {
          name,
          workType: workType.trim() || null,
          steps: steps.map((s) => ({ name: s.name, weight: w(s.weight), closeBy: s.closeBy })),
        },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['progress-rules'] });
      toast('Правило сохранено');
      onClose();
    },
    onError: (e) => toast(e.message, 'danger'),
  });
  const upd = (i: number, x: Partial<Step>) => setSteps(steps.map((s, j) => (j === i ? { ...s, ...x } : s)));
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      width="lg"
      title={rule ? 'Правило начисления' : 'Новое правило начисления'}
      description="Шаги с весами: процент работы — сумма весов закрытых шагов. Сумма весов — 100 %."
      footer={
        <>
          <Button onClick={onClose} className="max-md:h-11">
            Отмена
          </Button>
          <Button
            variant="primary"
            loading={save.isPending}
            disabled={
              !name.trim() || total !== 100 || steps.some((s) => !s.name.trim() || !(w(s.weight) > 0))
            }
            onClick={() => save.mutate()}
            className="max-md:h-11"
          >
            Сохранить
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap gap-3">
          <LField label="Название">
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-64 max-w-full max-md:h-11"
            />
          </LField>
          <LField label="Вид работ графика" hint="Работам этого вида правило предлагается само">
            <Input
              value={workType}
              onChange={(e) => setWorkType(e.target.value)}
              placeholder="Например, Трубопровод"
              className="w-56 max-w-full max-md:h-11"
            />
          </LField>
        </div>
        <ul className="flex flex-col gap-2">
          {steps.map((s, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: шаги правила без своего id, порядок задаёт человек
            <li key={i} className="flex flex-wrap items-end gap-2 rounded-md border border-line px-2 py-2">
              <LField label={`Шаг ${i + 1}`}>
                <Input
                  value={s.name}
                  onChange={(e) => upd(i, { name: e.target.value })}
                  className="w-48 max-w-full max-md:h-11"
                />
              </LField>
              <LField label="Вес, %">
                <Input
                  inputMode="decimal"
                  value={s.weight}
                  onChange={(e) => upd(i, { weight: e.target.value })}
                  className="w-20 max-md:h-11"
                />
              </LField>
              <LField label="Чем закрывается">
                <Select
                  value={s.closeBy}
                  onChange={(e) => upd(i, { closeBy: e.target.value as StepCloser })}
                  className="max-md:h-11"
                >
                  {Object.entries(STEP_CLOSERS).map(([k, t]) => (
                    <option key={k} value={k}>
                      {t}
                    </option>
                  ))}
                </Select>
              </LField>
              <IconButton
                label="Убрать шаг"
                disabled={steps.length <= 1}
                onClick={() => setSteps(steps.filter((_, j) => j !== i))}
                className="max-md:size-11"
              >
                <Trash2 size={16} />
              </IconButton>
            </li>
          ))}
        </ul>
        <div className="flex flex-wrap items-center gap-3 text-[14px]">
          <Button
            size="sm"
            disabled={steps.length >= 30}
            onClick={() => setSteps([...steps, { name: '', weight: '0', closeBy: 'manual' }])}
            className="max-md:h-11"
          >
            Добавить шаг
          </Button>
          <span className={total === 100 ? 'text-ink-2' : 'text-danger'}>
            Сумма весов: {String(total).replace('.', ',')} %
          </span>
        </div>
        <p className="text-[12px] text-ink-2">
          Шаг «акт» закрывается подписанным актом освидетельствования по работе. Шаги «отчёт смены» и «точка
          плана контроля» до подключения этих разделов закрывает вручную тот, кто правит график.
        </p>
      </div>
    </Dialog>
  );
}
